// @flow
import {logExperiment as abacusClientUtilsLogExperiment} from '@homeaway/abacus-client-utils';
import lowPriorityWarning from '@homeaway/low-priority-warning';

import {ON, OFF} from './constants';

import type {
    AbacusBucketConfig,
    AbacusEvaluationData,
    ExperimentVariant
} from './types';

// This is wacky but couldn't get a wrapper HOF typed correctly any other way.
// `(any) => mixed` is shorthand for "any function" (`Function` is alias to just
// `any`, so is not recommended).
//
// I have no idea why an external declaration works when regular typedefs don't.
// I think the tradeoff is that the function body below doesn't have to adhere
// these typings.
//
// c.f. https://github.com/flow-typed/flow-typed/issues/578#issuecomment-272863713
declare function deprecate<T>(
    fn: T,
    msg: string,
    shouldWarn: ?(void) => boolean
): T;

const loggedDeprecations: {[string]: boolean} = {};

/**
 * Logs a deprecation notice at most once per unique message.
 * @param {function} fn
 * @param {string} msg
 */
/* eslint-disable no-redeclare */
export function deprecate(fn: (mixed[]) => mixed, msg: string) {
    /* eslint-enable no-redeclare */
    return (...args) => {
        const hasAlreadyWarned = loggedDeprecations.hasOwnProperty(msg);
        lowPriorityWarning(!hasAlreadyWarned, msg);
        loggedDeprecations[msg] = true;
        return fn(...args);
    };
}

export function evaluationIsVariant(
    evaluationData?: AbacusEvaluationData,
    variant?: ExperimentVariant
) {
    const evaluationValue = evaluationData ? evaluationData.value : 0;

    if (variant === ON) {
        return evaluationValue > 0;
    }

    if (variant === OFF) {
        return evaluationValue === 0;
    }

    if (Array.isArray(variant)) {
        return variant.indexOf(evaluationValue) !== -1;
    }

    return variant === evaluationValue;
}

export function logExperiment(evaluationData: AbacusEvaluationData) {
    const {
        experimentKey: {experimentName},
        value
    } = evaluationData;

    abacusClientUtilsLogExperiment(evaluationData);

    // istanbul ignore next
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info(
            `%cLogged experiment: %c${experimentName} {${value}}`,
            'font-weight: bold',
            'font-weight: normal'
        );
    }
}

export function parseBucketConfig(bucketConfig: AbacusBucketConfig): mixed {
    if (!bucketConfig) {
        return bucketConfig;
    }
    try {
        return JSON.parse(bucketConfig);
    } catch (e) {
        return bucketConfig;
    }
}
